/* eslint-disable @typescript-eslint/no-unused-vars */
import Tree from 'react-d3-tree';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Divider, LinearProgress, Typography, useMediaQuery, useTheme } from '@mui/material';

import { LayoutBase } from '../../../../../layouts';
import { VMenu } from '../../../../../components/shared';
import { ToolsDamInformation } from '../../../../../components/custom';
import { Add, ListAltOutlined } from '@mui/icons-material';
import { useEffect, useRef, useState } from 'react';
import { IProfessionals, ProfessionalsServices } from '../../../../../services/api/professionals/ProfessionalsServices';
import { useAuthContext } from '../../../../../context';
import html2canvas from 'html2canvas';
import { FileServicesBack, IFileServices } from '../../../../../services/api/files/FileServicesBack';
import { GroupOutlined } from '@mui/icons-material';


interface TreeNode {
  name: string;
  children?: TreeNode[];
  attributes?: {
    Cargo?: string;
    Formação?: string;
  };
}

interface CustomNodeLabelProps {
  nodeDatum: TreeNode;
  toggleNode: () => void;
}

export const OrganizationChart = () => {

  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const url = useParams();
  const [dataTree, setDataTree] = useState<TreeNode>();
  const { userData } = useAuthContext();
  const permission = userData?.permissoes;
  const dateTime = new Date();
  const contentRef = useRef<HTMLDivElement>(null);
  const [imageData, setImageData] = useState<string>();

  const [img, setImg] = useState<IFileServices>({
    tipo: ['Estrutura Organizacional'],
    titulo: 'Estrutura Organizacional',
    lista_dam: url.id ? [url.id] : [],
    classificacao: ['informacoes_generales'],
    nome_arquivo: 'Estrutura Organizacional',
    descricao: 'Estrutura Organizacional',
    data_emissao: dateTime.getFullYear() + '-' + String(dateTime.getMonth() + 1).padStart(2, '0') + '-' + String(dateTime.getDate()).padStart(2, '0')
  });

  useEffect(() => {
    fectchData();
  }, [url]);

  // useEffect(() => {
  //   saveImg();
  // }, [img.file]);

  const handleSaveAsVariable = async () => {
    if (contentRef.current) {
      const canvas = await html2canvas(contentRef.current, {
        useCORS: true,
        logging: false
      });

      const imgDataURL = canvas.toDataURL('image/jpeg');
      setImageData(imgDataURL);
      const teste = dataURLToFile(imgDataURL, 'Estrutura Organizacional.jpeg');
      if (teste !== null) {
        setImg(prevData => ({
          ...prevData,
          file: teste
        }));
      }
    }
  };

  // async function saveImg() {
  //   if (img.file) {
  //     await FileServicesBack.create(img);
  //   }
  // }

  const dataURLToFile = (dataURL: string, fileName: string): File | null => {
    const arr = dataURL.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);

    if (!mimeMatch) {
      return null;
    }

    const mime = mimeMatch[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], fileName, { type: mime });
  };


  const fectchData = () => {
    if (url && url.id) {
      ProfessionalsServices.get(url.id, 'uuid')
        .then((result) => {
          if (result instanceof Error) {
            alert(result.message);
          } else {
            const dataArray = Object.values(result);
            if (dataArray.length > 0) {
              const data: TreeNode | undefined = createTreeFromProfessionals(dataArray); 
              setDataTree(data);
              if (data) {
                setTimeout(() => {
                  handleSaveAsVariable();
                }, 1000);
              }
            }
          }
        });
    }
  };

  function mapProfessionalToTreeNode(professional: IProfessionals): TreeNode {
    const activeSubordinates = professional.subordinado?.filter(sub => sub.status === true);

    return {
      name: professional.nome,
      children: activeSubordinates?.map(mapProfessionalToTreeNode),
      attributes: {
        Cargo: professional.role,
        Formação: professional.curriculum
      },
    };
  }

  function createTreeFromProfessionals(professionals: IProfessionals[]): TreeNode | undefined {
    const master = professionals.find(professional => !professional.id_superior);

    if (!master || master.status === false) {
      return undefined;
    }
    return mapProfessionalToTreeNode(master);
  }


  const myTreeConfig = {
    nodeSize: { x: 300, y: 200 },
    initialDepth: 3,
    svgProps: {
      transform: 'rotate(90)',
    },
  };

  const nodeFillColor = theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[400];
  const nodeStrokeColor = theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[600];

  const renderCustomNodeElement = ({ nodeDatum, toggleNode }: CustomNodeLabelProps) => (
    <g onClick={toggleNode}>
      <circle r={20} fill={nodeFillColor} stroke={nodeStrokeColor} strokeWidth={1.5} />

      <text fill={theme.palette.text.primary} strokeWidth="0" x="25" y="5" style={{ fontSize: '1rem', fontWeight: 'bold' }}>
        {nodeDatum.name}
      </text>
      {nodeDatum.attributes?.Cargo && (
        <text fill={theme.palette.text.secondary} strokeWidth="0" x="25" y="25" style={{ fontSize: '0.85rem' }}>
          Cargo: {nodeDatum.attributes.Cargo}
        </text>
      )}
      {nodeDatum.attributes?.Formação && (
        <text fill={theme.palette.text.secondary} strokeWidth="0" x="25" y="40" style={{ fontSize: '0.85rem' }}>
          Formação: {nodeDatum.attributes.Formação}
        </text>
      )}
    </g>
  );

  return (
    <LayoutBase title='Informações' tollBar={<ToolsDamInformation />}>
      <Box display="flex" flexDirection="column" height="100%">
        <Box>
          <VMenu id={url.id} />
          <Divider flexItem orientation='horizontal' />
          {permission !== undefined ?
            <Box justifyContent={'end'} alignContent={'end'} display={'flex'} gap={1} marginY={1} marginX={1}>
              <Button variant='outlined' sx={{ borderRadius: '25px' }} startIcon={<ListAltOutlined />} onClick={() => navigate('./professionals')}>Lista de Empregados</Button>
              {permission.includes('add') ?
                <Button variant='contained' sx={{ borderRadius: '25px' }} startIcon={<Add />} onClick={() => navigate('./professionals/new')}>Novo Empregado</Button> : null
              }
            </Box>
            :
            <LinearProgress />
          }
        </Box>

        {dataTree ? (
          <>
            <Box flex="1" p={1} height="100%" display="flex" justifyContent="center" alignItems="center" ref={contentRef} id="tree-container">
              <Tree
                enableLegacyTransitions={true}
                data={dataTree}
                orientation="vertical"
                pathFunc={'diagonal'}
                centeringTransitionDuration={3}
                translate={!smDown ? { x: 450, y: 50 } : { x: 150, y: 50 }}
                {...myTreeConfig}
                renderCustomNodeElement={renderCustomNodeElement}
              />
            </Box>
          </>
        ) : (
          <Box
            flex="1"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            p={3}
            textAlign="center"
          >
            <GroupOutlined sx={{ fontSize: 80, color: theme.palette.text.secondary, marginBottom: 2 }} />

            <Typography variant="h5" color="textSecondary" gutterBottom>
              Ainda não há funcionários cadastrados no organograma ou o funcionário principal está inativo.
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              Comece adicionando o primeiro funcionário para visualizar a estrutura da equipe.
            </Typography>
            {permission?.includes('add') && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate('./professionals/new')}
                sx={{ borderRadius: '25px', marginTop: 2 }}
              >
                Adicionar Novo Funcionário
              </Button>
            )}
          </Box>
        )}
      </Box>
    </LayoutBase>
  );
};